import {parseJwt, validJWTToken} from "../helper/JWT";
import {TokenRefreshLink} from "apollo-link-token-refresh";

/**
 *
 * @class BaseAuthenticationManager
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/manager/BaseAuthenticationManager.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export class BaseAuthenticationManager {
  constructor(store, apiRouter, router, currentVersion) {

    /** @type Vuex */
    this._store = store;

    /** @type number|null */
    this._autoLogoutInterval = null;

    /** @type number|null  */
    this._timeout = null;

    /** @type number|null  */
    this._windowPopupDuration = null;

    /** @type number|null  */
    this._remainingSeconds = null;

    /** @type boolean */
    this._refreshEventsActivated = false;

    /** @type Vuex */
    this._apiRouter = apiRouter;

    this._router = router;

    this._currentVersion = currentVersion;
  }

  logout() {
    return this._store.dispatch('resetState')
  }

  getToken() {
    return this._store.state.authToken
  }

  getRefreshToken() {
    return this._store.state.authRefreshToken
  }

  getTokenData() {
    return this._store.state.tokenData
  }

  isLoggedIn() {
    return this.getToken() && validJWTToken(this.getTokenData())
  }

  drawRefreshWindow(windowPopupDuration) {
    this._store.commit('setShowLogoutRemainingTime', windowPopupDuration)
  }

  resetAutoLogout() {
    if (this._timeout){
      this._store.commit('setShowLogoutRemainingTime', 0)
      this.startAutoLogout(this._timeout, this._windowPopupDuration)
    }
  }

  startAutoLogout(timeout = 20, windowPopupDuration = null) {
    this._timeout = this._remainingSeconds = timeout
    this._windowPopupDuration = windowPopupDuration;

    if (!this._autoLogoutInterval) {
      this._autoLogoutInterval = setInterval(() => {
        if (this._remainingSeconds <= 0) {
          this.stopAutoLogout()

          if (this._windowPopupDuration){
            this.drawRefreshWindow(this._windowPopupDuration)
          }else{
            this.logout()
          }
        } else {
          this._remainingSeconds--
        }
      }, 1000)
    }

    if (!this._refreshEventsActivated) {
      window.addEventListener('mousemove', e => {
        this.refreshAutoLogout()
      })

      this._refreshEventsActivated = true
    }

    this.refreshAutoLogout()
  }

  stopAutoLogout() {
    if (this._autoLogoutInterval !== null)
      clearInterval(this._autoLogoutInterval)

    this._autoLogoutInterval = null
  }

  refreshAutoLogout() {
    this._remainingSeconds = this._timeout
  }

  createRefreshLink() {
    return new TokenRefreshLink({
      isTokenValidOrUndefined: () => this.isLoggedIn(),
      fetchAccessToken: () => this.fetchNewTokens(this.getRefreshToken()),
      handleResponse: (operation, accessTokenField) => tokenObject => {
        if (tokenObject.error) {
          this._store.dispatch('logout')
        } else {
          // The authorization header of the API call needs to be updated with the new token received from refresh
          // Because the call was already fired BEFORE the store was updated with the new token (dispatch login below)
          if (operation.getContext()?.headers){
            operation.getContext().headers.authorization = `Bearer ${tokenObject.authToken}`;
          }

          this._store.dispatch('login', tokenObject)
        }

        return {
           access_token: tokenObject.authToken
        }
      },
      handleError: err => {
        this._store.dispatch('logout')
        this._router.push({ name: 'Login' });
      }
    });
  }

  refreshToken() {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken){
      return Promise.resolve({
        error: 'No refresh token in store',
        authToken : null,
        refreshToken : null,
        tokenData : null,
      });
    }

    return this.fetchNewTokens(refreshToken).then(tokenObject => {
      this._store.dispatch('login', tokenObject)

      return tokenObject;
    });
  }

  fetchNewTokens(refreshToken) {
    return fetch(this._apiRouter.getRoute('refreshToken'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'current-version': this._currentVersion
      },
      body: JSON.stringify({
        refresh_token : refreshToken
      }),
    })
      .then(response => response.json())
      .then(data => {
        return {
          error: null,
          authToken : data && data.token ? data.token : null,
          refreshToken : data && data.refresh_token ? data.refresh_token : null,
          tokenData : data && data.token ? parseJwt(data.token) : null,
        }
      })
      .catch((error) => {
        return {
          error: error,
          authToken : null,
          refreshToken : null,
          tokenData : null,
        }
      });
  }
}